import { Layout, Space, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { getAttributeValue } from "utils/utils";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const BUSINESS_PROGRAM = "nBX5Jeo69up";
const CREATION_CONTRIBUTION_ATTR = "Has the district contributed to the creation of this business";
const SUPPORT_STORAGE_ELEMENT = "ZsVGR44uSJs";
const SUPPORT_MACHINERY_ELEMENT = "CuiQEA6mkjk";
const SUPPORT_EVIDENCE_ELEMENT = "E6mSis1p8NG";
const SUPPORT_DETAILS_ELEMENT = "TsTYaz7dtAn";

function isTruthyAttributeValue(value) {
    return value === "true" || value === true;
}

function isNewBusinessCreated(business) {
    return isTruthyAttributeValue(getAttributeValue(CREATION_CONTRIBUTION_ATTR, business));
}

function hasDistrictSupport(business, reports) {
    const investment = getAttributeValue("District investment Details", business);
    if (investment && investment !== "N/A" && String(investment).trim() !== "") {
        return true;
    }

    const currentReport = reports.find((report) => report.trackedEntity === business.trackedEntity);
    if (!currentReport?.dataValues?.length) {
        return false;
    }

    return currentReport.dataValues.some((value) =>
        (value.dataElement === SUPPORT_STORAGE_ELEMENT && isTruthyAttributeValue(value.value))
        || (value.dataElement === SUPPORT_MACHINERY_ELEMENT && isTruthyAttributeValue(value.value))
        || value.dataElement === SUPPORT_EVIDENCE_ELEMENT
        || (value.dataElement === SUPPORT_DETAILS_ELEMENT && value.value),
    );
}

function buildBusinessRow(business) {
    return {
        key: business.trackedEntity,
        support: getAttributeValue("District investment Details", business),
        sector: getAttributeValue("Business Category", business),
        scale: getAttributeValue("DCACT | Scale of Operation", business),
        jobsCreated: getAttributeValue("Number of Jobs Created", business),
    };
}

const BusinessAndJobPromotion = forwardRef(({
    year,
    district,
    hideComment,
    savedAssessmentData,
    assessmentStartLoading,
    isAssessmentStarted,
}, ref) => {

    const [data, setData] = useState([]);
    const [summary, setSummamry] = useState([]);
    const [scorei, setScorei] = useState(0);
    const [maxScore, setMaxScore] = useState(1);
    const { Header, Content } = Layout;
    const { Title, Text } = Typography;

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "SDI6",
            area: "Local Economic Development (LED)",
            maxScore,
            data,
            summary,
            scorei
        }),
    }));

    const DPAT_SLUG = "sdi6.0-6.2";

    const loadFromDhis2 = useCallback(() => {
        axios
            .get(`/tracker/trackedEntities?orgUnit=${district}&program=${BUSINESS_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31`)
            .then(result => {
                const businessInstances = result.data?.instances || [];
                if (businessInstances.length === 0) {
                    setData([]);
                    setSummamry([]);
                    setScorei(0);
                    return;
                }

                axios
                    .get(`/tracker/events?program=${BUSINESS_PROGRAM}&orgUnit=${district}&startDate=${year}-01-01&endDate=${year}-12-31`)
                    .then(resp => {
                        const reports = resp.data.instances || [];
                        const newBusinesses = businessInstances.filter(isNewBusinessCreated);
                        const existingBusinessesSupported = businessInstances.filter(
                            (business) => !isNewBusinessCreated(business) && hasDistrictSupport(business, reports),
                        );

                        const temp = [
                            ...newBusinesses.map(buildBusinessRow),
                            ...existingBusinessesSupported.map(buildBusinessRow),
                        ];

                        setData(temp);
                        setSummamry([{
                            key: "sdi-6-2-summary",
                            no: 1,
                            noOfExistingBusinessesSupported: existingBusinessesSupported.length,
                            noOfBusinessCreated: newBusinesses.length,
                        }]);

                        if (temp.length > 0) {
                            setScorei(1);
                        } else {
                            setScorei(0);
                        }
                    })
                    .catch(err => console.log(err));
            })
            .catch(err => console.log(err));
    }, [district, year]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2,
        applySnapshot: (snap) => {
            if (snap.data) setData(snap.data);
            if (snap.summary) {
                setSummamry(
                    snap.summary.map((row) => ({
                        ...row,
                        key: row.key || "sdi-6-2-summary",
                        noOfExistingBusinessesSupported:
                            row.noOfExistingBusinessesSupported
                            ?? row.noOfExistingBusinessSupported
                            ?? 0,
                        noOfBusinessCreated: row.noOfBusinessCreated ?? 0,
                    })),
                );
            }
            if (snap.scorei != null) setScorei(snap.scorei);
            if (snap.maxScore != null) setMaxScore(snap.maxScore);
        },
        deps: [year, district, loadFromDhis2],
    });

    const dataColumn = [
        {
            title: "Activity / Type of support",
            dataIndex: "support",
            key: "support"
        },
        {
            title: "Scale of Business",
            dataIndex: "scale",
            key: "scale"
        },
        {
            title: "Sector",
            dataIndex: "sector",
            key: "sector"
        },
        {
            title: "No. of new Jobs Created",
            dataIndex: "jobsCreated",
            key: "jobsCreated"
        }
    ];

    const summaryColumn = [
        {
            title: "No",
            dataIndex: "no",
            key: "no"
        },
        {
            title: "Number of existing businesses supported",
            dataIndex: "noOfExistingBusinessesSupported",
            key: "noOfExistingBusinessesSupported"
        },
        {
            title: "Number of new businesses created",
            dataIndex: "noOfBusinessCreated",
            key: "noOfBusinessCreated"
        }
    ];

    return (
        <Comment
            data={data}
            year={year}
            districtId={district}
            tableCommentedId={`sdi6.0-6.2-${year}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={year}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "30px" }}
                    />

                    <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                              <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 6.0-6.2 Actual Score:" score={scorei} />
                    </IndicatorScoreSection>
                    <Title level={4} style={{ marginTop: "20px" }}>Evidence of Business and Jobs Creation</Title>
                    <Table
                        columns={dataColumn}
                        dataSource={data}
                        pagination={false}
                        bordered
                        rowKey="key"
                    />

                    <Title level={4} style={{ marginTop: "20px" }}>Summary of Business Creation</Title>
                    <Table
                        columns={summaryColumn}
                        dataSource={summary}
                        pagination={false}
                        bordered
                        rowKey="key"
                    />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
})

export default BusinessAndJobPromotion;